import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { 
  Security as SecurityIcon,
  FitnessCenter as FitnessCenterIcon,
  Build as BuildIcon,
  Loop as LoopIcon
} from '@mui/icons-material';
import { 
  FaShieldAlt, 
  FaHardHat, 
  FaExclamationTriangle,
  FaUserShield,
  FaWeightHanging,
  FaRedoAlt,
  FaPeopleCarry,
} from 'react-icons/fa';
import { 
  TbProgressAlert 
} from 'react-icons/tb';
import marketingTheme from '../themes/marketingTheme';
import SafetyZoneVisualization from '../components/SafetyZoneVisualization';
import { SmartCTA } from '../components/SmartCTAs';

const WorkerSafety = () => {
  const features = [
    {
      icon: <TbProgressAlert style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Proactive Indication',
      description: 'Real-time Audible Alerts Based on Risk Factors'
    },
    {
      icon: <FaPeopleCarry style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Lumbar Risk',
      description: 'Mitigating the Potential for Lower Back Injuries During Lifting, Bending, and Twisting'
    },
    {
      icon: <FaUserShield style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Forklift Safety',
      description: 'Minimising Risks and Incidents Involving Forklifts and Similar Powered Industrial Equipment'
    },
    {
      icon: <FaHardHat style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'PPE Compliance',
      description: 'Ensuring Adherence to Personal Protective Equipment (PPE) Guidelines in Designated Areas'
    },
    {
      icon: <FaRedoAlt style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Repetitive Motion Strain',
      description: 'Providing Timely Notifications on Repetitive Motion Strain Risk Based on Joint Movements'
    },
    {
      icon: <FaWeightHanging style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Crane Safety',
      description: 'Enhancing Safety Measures for Workers Engaged in Crane Operations or in Proximity to Crane Activities'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 8,
          position: 'relative',
          p: 6
        }}>
          <Typography variant="h1" sx={{ 
            color: 'white', 
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700
          }}>
            AI Vision for Worker Safety
          </Typography>
          <Typography variant="h5" sx={{ 
            color: marketingTheme.palette.primary.main, 
            mb: 4,
            fontWeight: 400
          }}>
            Vision AI for Worker Safety.
          </Typography>
        </Box>

        {/* Main Content with Statistics */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={6} sx={{ mb: 6 }}>
            {/* Left side - Main content */}
            <Grid item xs={12} md={7}>
              <Typography variant="h4" sx={{ color: 'white', mb: 3 }}>
                Prevent Injuries, Protect Worker Safety
              </Typography>
              
              <Typography variant="body1" sx={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                fontSize: '1.2rem',
                lineHeight: 1.8,
                mb: 4
              }}>
                Traditional safety programs rely on lagging indicators—measuring what went wrong after injuries occur. 
                MotionLogic® shifts to leading indicators, identifying hazards in real-time and helping to prevent injuries.
              </Typography>
            </Grid>

            {/* Right side - Statistics */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ 
                backgroundColor: '#2d2d33', 
                border: '1px solid rgba(255, 255, 255, 0.1)',
                p: 4, 
                textAlign: 'center',
                height: '100%'
              }}>
                <Typography variant="h4" sx={{ color: 'white', mb: 3 }}>
                  The Cost of Workplace Injuries
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="h3" sx={{ color: '#eb4d4b', fontWeight: 'bold' }}>
                      15K
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      Serious Injury Claims in Manufacturing Per Year (AU)
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ color: '#eb4d4b', fontWeight: 'bold' }}>
                      A$1M
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      Per Death
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ color: '#eb4d4b', fontWeight: 'bold' }}>
                      A$11,803
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      Median Compensation Paid Per Injury
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Features Section */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h3" sx={{ 
              color: 'white', 
              textAlign: 'center', 
              mb: 6,
              fontWeight: 600
            }}>
              Purpose
            </Typography>
            
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    height: '100%',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 25px rgba(237, 185, 46, 0.3)'
                    }
                  }}>
                    <CardContent sx={{ 
                      p: 4, 
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Box sx={{ mb: 3 }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h5" sx={{ 
                        color: 'white', 
                        mb: 2,
                        fontWeight: 600
                      }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: 1.6
                      }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
          
          {/* 3D Visualization Section */}
          <Paper sx={{ 
            backgroundColor: '#2d2d33', 
            border: '1px solid rgba(255, 255, 255, 0.1)',
            p: { xs: 3, md: 6 },
            textAlign: 'center',
            mb: 6,
            width: '100%',
            minHeight: '500px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Typography variant="h4" sx={{ 
              color: 'white', 
              mb: 3,
              fontWeight: 600
            }}>
              AI-Powered Safety Zone Monitoring
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              mb: 4,
              maxWidth: '600px',
              mx: 'auto'
            }}>
              {/* Real-time visualization of worker safety zones with AI-powered motion tracking and risk assessment */}
              MotionLogic allows you to observe the workplace 24/7, spot/magnify where issues are occurring, and do this over a period of time to identify patterns of ergonomic activity that may lead to injury.
            </Typography>
            {/* 3D Visualization */}
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SafetyZoneVisualization />
            </Box>
          </Paper>

          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 4, 
                backgroundColor: '#2d2d33',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%'
              }}>
                <Typography variant="h5" sx={{ color: marketingTheme.palette.primary.main, mb: 3, fontWeight: 600 }}>
                  What Safety Managers Need
                </Typography>
                <Box component="ul" sx={{ pl: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
                  <li>Proactive identification of safety risks</li>
                  <li>Quantitative analysis of key risk factors</li>
                  <li>Objective data to support safety initiatives</li>
                  <li>Evidence-based insights for continuous improvement</li>
                  <li>Real-time comprehension of safety scenarios</li>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 4, 
                backgroundColor: '#2d2d33',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                height: '100%'
              }}>
                <Typography variant="h5" sx={{ color: '#1dd1a1', mb: 3, fontWeight: 600 }}>
                  What Operations Executives Need
                </Typography>
                <Box component="ul" sx={{ pl: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
                  <li>Reduced insurance premiums through demonstrable risk reduction</li>
                  <li>Reduction in preventable injuries</li>
                  <li>Reduction in lost time injuries and associated costs</li>
                  <li>Quantifiable ROI from safety technology investments</li>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img 
              src="/images/worker_ergonomics.png" 
              alt="AI Vision for Worker Safety" 
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }} 
            />
          </Box>

          <Typography variant="body1" sx={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            fontSize: '1.1rem',
            lineHeight: 1.8,
            mb: 4
          }}>
            MotionLogic® provides proactive reporting on safety events and near misses. Enabling processes to be made safer.
            <br /><br />
            MotionLogic® observes manual tasks. Visually inferring repetitive movement, proximity to hazardous tools, sustained or awkward postures, proximity to hot work, or proximity to live work.
            <br /><br />
            Identifying tasks that cause fatigue, and thus have a heightened risk of injury.
            <br /><br />
            Proactive reporting profiles the extent and frequency of key risks, and provide real-time audible alerts based upon risk factors.
            <br /><br />
            Strengthening EHS compliance with AI-powered visual analysis.
          </Typography>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <SmartCTA
              context="experience-demo"
              fallbackHref="/contact"
              fallbackText="Learn More"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: marketingTheme.palette.primary.main,
                color: '#000',
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: marketingTheme.palette.primary.dark,
                },
              }}
            />
          </Box>
        </Box>

        {/* Privacy Notice */}
        <Paper sx={{ 
          backgroundColor: 'rgba(29, 209, 161, 0.1)', 
          border: '1px solid rgba(29, 209, 161, 0.3)',
          p: 4, 
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <FaShieldAlt style={{ fontSize: 60, color: '#1dd1a1', flexShrink: 0 }} />
          <Box>
            <Typography variant="h5" sx={{ 
              color: '#1dd1a1',
              fontWeight: 600,
              mb: 2
            }}>
              Pixel Privacy
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6
            }}>
              MotionLogic's camera technology preserves the privacy of individuals in visual scenes.
              We pseudonymise our data, and remove individually identifying features, including blurring faces.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default WorkerSafety;
